import React, { useState, useEffect, useRef } from 'react';
import {
  Target,
  DollarSign,
  CreditCard,
  Calculator,
  TrendingUp,
  TrendingDown,
  PieChart,
  BarChart3,
  FileText,
  Eye,
  Trash2,
  Edit,
  Save,
  X,
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Bookmark,
  Star,
  Share,
  Info,
  ExternalLink,
  Archive,
  Send,
  Flag,
  Heart,
  ThumbsUp,
  ThumbsDown,
  Upload,
  FileUp,
  FileDown,
  FileCheck,
  FileX,
  Volume2,
  VolumeX,
  Headphones,
  Speaker,
  Radio,
  Music,
  Video,
  Camera,
  Image,
  File,
  Folder,
  FolderOpen,
  HardDrive,
  Cloud,
  Wifi,
  WifiOff,
  Signal,
  Battery,
  BatteryLow,
  Power,
  PowerOff,
  Activity,
  Zap,
  Shield,
  Lock,
  Key,
  Users,
  Building,
  Calendar,
  Phone,
  Mail,
  MapPin,
  CheckSquare,
  Square as SquareIcon,
  ArrowUpRight,
  ArrowDownRight,
  ArrowLeft,
  Home,
  Wrench,
  FileBarChart,
  Cog,
  Settings,
  Link,
  Monitor,
  Tablet,
  Smartphone,
  Lightbulb,
  Cpu,
  Network,
  Database,
  Layers,
  User,
  Languages,
  Mic,
  Play,
  Pause,
  Square,
  RefreshCw,
  Copy,
  Download,
  Globe,
  Users2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Brain,
  MessageSquare,
  AlertCircle,
  CheckSquare as CheckSquareIcon,
  Receipt,
  Bot,
  Scan,
  Zap as ZapIcon,
  Shield as ShieldIcon,
  LineChart,
  BarChart,
  PieChart as PieChartIcon,
  AreaChart,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowUp,
  ArrowDown,
  Minus,
  Percent,
  Calendar as CalendarIcon,
  Clock as ClockIcon,
  AlertCircle as AlertCircleIcon,
  Info as InfoIcon,
  Lightbulb as LightbulbIcon,
  Award,
  Gift,
  Coins,
  Wallet,
  PiggyBank,
  Banknote,
  Receipt as ReceiptIcon,
  ShoppingCart,
  Package,
  Truck,
  Building2,
  Factory,
  Store,
  Home as HomeIcon,
  Car,
  Plane,
  Train,
  Ship,
  Fuel,
  Wrench as WrenchIcon,
  Tool,
  Hammer,
  Screwdriver,
  Cog as CogIcon,
  Settings as SettingsIcon,
  HelpCircle,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import { useToast } from '../common/Toast';
import { spendOptimizationService } from '../../services/spendOptimizationService';

// Custom Dropdown Component for Spend Analysis Actions
const SpendAnalysisActionDropdown = ({
  analysis,
  onView,
  onEdit,
  onImplement,
  onDelete,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (analysis && (analysis.id || analysis._id)) {
      onView(analysis);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (analysis && (analysis.id || analysis._id)) {
      onEdit(analysis);
    }
  };

  const handleImplement = () => {
    setIsOpen(false);
    if (analysis && (analysis.id || analysis._id)) {
      onImplement(analysis);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (analysis && (analysis.id || analysis._id)) {
      onDelete(analysis);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen && (
        <ul
          className="dropdown-menu dropdown-menu-end show"
          style={{
            position: 'fixed',
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${
                  window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8
                }px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            right: `${
              window.innerWidth -
              buttonRef.current.getBoundingClientRect().right
            }px`,
            zIndex: 9999,
            minWidth: '180px',
            boxShadow:
              '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '0',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0,
          }}
        >
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleView}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Eye
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              View Details
            </button>
          </li>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleEdit}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Edit
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Edit Analysis
            </button>
          </li>
          {analysis?.status === 'Identified' && (
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={handleImplement}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <Target
                  size={16}
                  style={{
                    marginRight: '10px',
                    color: '#6b7280',
                    flexShrink: 0,
                  }}
                />
                Implement Optimization
              </button>
            </li>
          )}
          <li
            style={{
              borderTop: '1px solid #e5e7eb',
              marginTop: '4px',
              paddingTop: '4px',
            }}
          >
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleDelete}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#dc2626',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#fef2f2';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <Trash2
                size={16}
                style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }}
              />
              Delete Analysis
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

// Custom Dropdown Component for Recommendation Actions
const RecommendationActionDropdown = ({ recommendation, onTrack }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 250;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleTrackSavings = () => {
    setIsOpen(false);
    if (recommendation && (recommendation.id || recommendation._id)) {
      onTrack(recommendation);
    }
  };

  return (
    <div
      className="dropdown"
      ref={dropdownRef}
      style={{ position: 'relative', zIndex: 1050 }}
    >
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          color: '#3A8DCC',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '32px',
          height: '32px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={(e) => {
          e.target.style.backgroundColor = '#E8F1FD';
          e.target.style.borderColor = '#3A8DCC';
        }}
        onMouseLeave={(e) => {
          e.target.style.backgroundColor = '#ffffff';
          e.target.style.borderColor = '#e5e7eb';
        }}
      >
        <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
      </button>
      {isOpen && (
        <ul className="dropdown-menu dropdown-menu-end show" style={{
          position: 'absolute',
          [openUpward ? 'bottom' : 'top']: '100%',
          right: '0',
          zIndex: 9999,
          minWidth: '180px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '4px 0',
          backgroundColor: 'white',
          listStyle: 'none',
          margin: '4px 0 0 0',
          overflow: 'hidden'
        }}>
          <li>
            <button
              className="dropdown-item d-flex align-items-center"
              onClick={handleTrackSavings}
              style={{
                padding: '10px 16px',
                fontSize: '14px',
                color: '#374151',
                border: 'none',
                backgroundColor: 'transparent',
                width: '100%',
                textAlign: 'left',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
                transition: 'background-color 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              <CheckCircle
                size={16}
                style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }}
              />
              Track Savings
            </button>
          </li>
        </ul>
      )}
    </div>
  );
};

const SpendOptimization = () => {
  const { success, error: showError } = useToast();
  const [activeTab, setActiveTab] = useState('analysis');
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showImplementModal, setShowImplementModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showAnalysisModal, setShowAnalysisModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [spendAnalysis, setSpendAnalysis] = useState([]);
  const [optimizationRecommendations, setOptimizationRecommendations] =
    useState([]);
  const [savingsTracking, setSavingsTracking] = useState([]);
  const [spendAnalytics, setSpendAnalytics] = useState(null);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState('');
  const [analysisFormData, setAnalysisFormData] = useState({
    startDate: '',
    endDate: '',
    currency: 'USD',
  });
  const [savingsFormData, setSavingsFormData] = useState({
    actualSavings: '',
    status: 'Implemented',
    implementationDate: '',
  });

  const formatCurrency = (value, currency = 'USD') =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      value || 0
    );

  // Calculate statistics
  const stats = {
    totalAnalyses: spendAnalytics?.totalAnalyses || spendAnalysis.length,
    totalSpend: spendAnalytics?.totalSpend || 0,
    averageSpend: spendAnalytics?.averageSpend || 0,
    totalRecommendations: optimizationRecommendations.length,
    approvedRecommendations: optimizationRecommendations.filter(
      (r) => r.status === 'Approved'
    ).length,
    totalEstimatedSavings: optimizationRecommendations.reduce(
      (sum, r) => sum + (r.estimatedSavings || r.potentialSavings || 0),
      0
    ),
    totalActualSavings: savingsTracking.reduce(
      (sum, r) => sum + (r.actualSavings || 0),
      0
    ),
  };

  const tabs = [
    { id: 'analysis', label: 'Spend Analysis' },
    { id: 'recommendations', label: 'AI Recommendations' },
    { id: 'tracking', label: 'Savings Tracking' },
    { id: 'settings', label: 'Optimization Settings' },
  ];

  // Define columns for Spend Analysis DataTable
  const analysisColumns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'analysisPeriod',
      label: 'Analysis Period',
      width: '260px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {row.analysisPeriod?.startDate
              ? new Date(row.analysisPeriod.startDate).toLocaleDateString()
              : '—'}{' '}
            to{' '}
            {row.analysisPeriod?.endDate
              ? new Date(row.analysisPeriod.endDate).toLocaleDateString()
              : '—'}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.generatedBy || 'Manual'} • {row.currency || 'USD'}
          </div>
        </div>
      ),
    },
    {
      key: 'totalSpend',
      label: 'Total Spend',
      width: '140px',
      render: (value, row) => (
        <span style={{ fontWeight: '600', color: '#1f2937' }}>
          {formatCurrency(value, row.currency)}
        </span>
      ),
    },
    {
      key: 'spendByCategory',
      label: 'Top Category',
      width: '200px',
      render: (value, row) => {
        if (!Array.isArray(value) || value.length === 0) {
          return <span style={{ color: '#6b7280' }}>—</span>;
        }
        const topCategory = [...value].sort((a, b) => b.amount - a.amount)[0];
        return (
          <div>
            <div style={{ fontWeight: '500', color: '#1f2937' }}>
              {topCategory.category}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {formatCurrency(topCategory.amount, row.currency)} •{' '}
              {topCategory.percentage?.toFixed(1)}%
            </div>
          </div>
        );
      },
    },
    {
      key: 'spendByVendor',
      label: 'Top Vendor',
      width: '200px',
      render: (value, row) => {
        if (!Array.isArray(value) || value.length === 0) {
          return <span style={{ color: '#6b7280' }}>—</span>;
        }
        const topVendor = [...value].sort((a, b) => b.amount - a.amount)[0];
        return (
          <div>
            <div style={{ fontWeight: '500', color: '#1f2937' }}>
              {topVendor.vendor}
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {formatCurrency(topVendor.amount, row.currency)} •{' '}
              {topVendor.percentage?.toFixed(1)}%
            </div>
          </div>
        );
      },
    },
  ];

  const recommendationColumns = [
    {
      key: 'title',
      label: 'Recommendation',
      width: '320px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.recommendationType} • {row.category}
          </div>
        </div>
      ),
    },
    {
      key: 'potentialSavings',
      label: 'Estimated Savings',
      width: '160px',
      render: (value, row) => (
        <span style={{ fontWeight: '600', color: '#10b981' }}>
          {formatCurrency(row.estimatedSavings || value, row.currency || 'USD')}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (status) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor:
              status === 'Approved'
                ? '#d1fae5'
                : status === 'Implemented'
                ? '#e0f2fe'
                : '#fef3c7',
            color:
              status === 'Approved'
                ? '#059669'
                : status === 'Implemented'
                ? '#0284c7'
                : '#92400e',
          }}
        >
          {status}
        </span>
      ),
    },
    {
      key: 'priority',
      label: 'Priority',
      width: '120px',
      render: (priority) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor:
              priority === 'High' || priority === 'Critical'
                ? '#fee2e2'
                : priority === 'Medium'
                ? '#fef3c7'
                : '#d1fae5',
            color:
              priority === 'High' || priority === 'Critical'
                ? '#b91c1c'
                : priority === 'Medium'
                ? '#92400e'
                : '#065f46',
          }}
        >
          {priority}
        </span>
      ),
    },
    {
      key: 'implementationEffort',
      label: 'Effort',
      width: '120px',
      render: (effort) => (
        <span style={{ fontSize: '12px', color: '#6b7280' }}>{effort}</span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, row) => (
        <RecommendationActionDropdown
          recommendation={row}
          onTrack={handleTrackSavings}
        />
      ),
    },
  ];

  const trackingColumns = [
    {
      key: 'title',
      label: 'Recommendation',
      width: '320px',
      render: (value, row) => (
        <div>
          <div
            style={{ fontWeight: '500', color: '#1f2937', marginBottom: '4px' }}
          >
            {value}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {row.recommendationType} • {row.category}
          </div>
        </div>
      ),
    },
    {
      key: 'actualSavings',
      label: 'Actual Savings',
      width: '160px',
      render: (value, row) => (
        <span style={{ fontWeight: '600', color: '#10b981' }}>
          {formatCurrency(value, row.currency || 'USD')}
        </span>
      ),
    },
    {
      key: 'implementationDate',
      label: 'Implementation Date',
      width: '160px',
      render: (value, row) => {
        const dateValue = value || row.implementationDate;
        if (!dateValue) return <span style={{ fontSize: '12px', color: '#6b7280' }}>—</span>;
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return <span style={{ fontSize: '12px', color: '#6b7280' }}>—</span>;
          return <span style={{ fontSize: '12px', color: '#6b7280' }}>{date.toLocaleDateString()}</span>;
        } catch (e) {
          return <span style={{ fontSize: '12px', color: '#6b7280' }}>—</span>;
        }
      }
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: status === 'Implemented' ? '#e0f2fe' : '#fef3c7',
            color: status === 'Implemented' ? '#0284c7' : '#92400e',
          }}
        >
          {status || 'Pending'}
        </span>
      ),
    },
  ];

  // Handler functions
  const loadSpendAnalytics = async () => {
  try {
    setLoading(true);

    const response = await spendOptimizationService.getSpendAnalytics();
    
    if (response.success) {
      setSpendAnalytics(response.data);
    }

  } catch (error) {
    showError(error.message || 'Failed to load spend analytics');
  } finally {
    setLoading(false);
  }
};

  const loadSavingsTracking = async () => {
    try {
      const response = await spendOptimizationService.getSavingsTracking();
      if (response.success) {
        setSavingsTracking(response.data || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load savings tracking');
    }
  };

  const loadRecommendations = async (analysisId) => {
    if (!analysisId) {
      setOptimizationRecommendations([]);
      return;
    }
    try {
      const response = await spendOptimizationService.getRecommendations(
        analysisId
      );
      if (response.success) {
        setOptimizationRecommendations(response.data || []);
      }
    } catch (error) {
      showError(error.message || 'Failed to load recommendations');
    }
  };
  
  const loadSpendAnalysis = async () => {
  try {
    const response = await spendOptimizationService.getSpendAnalysis();

    if (response.success) {
      setSpendAnalysis(response.data || []);
    }

  } catch (error) {
    showError(error.message || 'Failed to load spend analyses');
  }
};

  // Persist analysis data to localStorage
  useEffect(() => {
    if (spendAnalysis.length > 0) {
      try {
        localStorage.setItem('spendOptimization_analysis', JSON.stringify(spendAnalysis));
      } catch (error) {
        console.warn('Failed to save analysis to localStorage:', error);
      }
    }
  }, [spendAnalysis]);

  useEffect(() => {
    loadSpendAnalysis();
    loadSpendAnalytics();
    loadSavingsTracking();
    
    // Restore persisted analysis data
    try {
      const savedAnalysis = localStorage.getItem('spendOptimization_analysis');
      if (savedAnalysis) {
        const parsed = JSON.parse(savedAnalysis);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setSpendAnalysis(parsed);
          if (!selectedAnalysisId && parsed[0]?._id) {
            setSelectedAnalysisId(parsed[0]._id);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to restore analysis from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    loadRecommendations(selectedAnalysisId);
  }, [selectedAnalysisId]);

  useEffect(() => {
    if (!selectedAnalysisId && spendAnalysis.length) {
      setSelectedAnalysisId(spendAnalysis[0]._id);
    }
  }, [spendAnalysis, selectedAnalysisId]);

  const handleTrackSavings = (recommendation) => {
    setSelectedItem(recommendation);
    setSavingsFormData({
      actualSavings: recommendation?.actualSavings ?? '',
      status: recommendation?.status || 'Implemented',
      implementationDate: recommendation?.implementationDate
        ? new Date(recommendation.implementationDate)
            .toISOString()
            .split('T')[0]
        : '',
    });
    setShowImplementModal(true);
  };

  const handleRefresh = async () => {
    setLoading(true);
    await loadSpendAnalysis();
    await loadSpendAnalytics();
    await loadSavingsTracking();
    await loadRecommendations(selectedAnalysisId);
    setLoading(false);
  };

  const handleHelpClick = () => {
    const helpUrl = '/help/spend-optimization';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  const handleNewAnalysis = () => {
    setAnalysisFormData({ startDate: '', endDate: '', currency: 'USD' });
    setShowAnalysisModal(true);
  };

  const handleGenerateRecommendations = () => {
    if (!selectedAnalysisId) {
      showError('Select a spend analysis before generating recommendations');
      return;
    }
    setShowGenerateModal(true);
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
      disabled: loading,
    },
    ...(activeTab === 'analysis'
      ? [
          {
            label: 'Run Analysis',
            icon: <Plus size={18} />,
            onClick: handleNewAnalysis,
            variant: 'primary',
            tooltip: 'Create a new spend analysis',
          },
        ]
      : activeTab === 'recommendations'
      ? [
          {
            label: 'Generate Recommendations',
            icon: <Plus size={18} />,
            onClick: handleGenerateRecommendations,
            variant: 'primary',
            tooltip: 'Generate AI recommendations',
          },
        ]
      : []),
  ];

  const handleImplementConfirm = async () => {
    setIsProcessing(true);
    try {
      if (!selectedItem?._id) {
        setShowImplementModal(false);
        return;
      }
      const payload = {
        actualSavings: Number(savingsFormData.actualSavings),
        status: savingsFormData.status,
        implementationDate: savingsFormData.implementationDate || undefined,
      };
      await spendOptimizationService.trackSavings(selectedItem._id, payload);
      success('Savings tracked successfully');
      await loadSavingsTracking();
      await loadRecommendations(selectedAnalysisId);
      setShowImplementModal(false);
      setSelectedItem(null);
    } catch (error) {
      showError(error.message || 'Failed to track savings');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveConfirm = async () => {
    setIsProcessing(true);
    try {
      const payload = { analysisId: selectedAnalysisId };
      const response = await spendOptimizationService.generateRecommendations(
        payload
      );
      if (response.success) {
        setOptimizationRecommendations(response.data || []);
        success('Recommendations generated successfully');
      }
      setShowGenerateModal(false);
    } catch (error) {
      showError(error.message || 'Failed to generate recommendations');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAnalysisSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        period: {
          startDate: analysisFormData.startDate,
          endDate: analysisFormData.endDate,
        },
        filters: {
          currency: analysisFormData.currency,
        },
      };
      const response = await spendOptimizationService.analyzeSpend(payload);
      if (response.success) {
        const analysis = response.data;
        setSpendAnalysis((prev) => [analysis, ...prev]);
        setSelectedAnalysisId(analysis._id);
        success('Spend analysis completed');
      }
      await loadSpendAnalytics();
      setShowAnalysisModal(false);
    } catch (error) {
      showError(error.message || 'Failed to run spend analysis');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Spend Optimization"
        description="AI-powered spend analysis and optimization recommendations for cost reduction and efficiency"
        actions={headerActions}
      />
      <div className="page-container">
        {/* KPI Cards Grid – StatCard reference styling */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {/* Total Analyses */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {stats.totalAnalyses}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Target size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalAnalyses}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Analyses
                </div>
              </div>
            </div>
          </div>

          {/* Total Spend */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {formatCurrency(stats.totalSpend)}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(stats.totalSpend)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Spend
                </div>
              </div>
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 text-[11px] font-semibold px-2 py-1 rounded bg-[#E8F5E9] text-[#166534]">
                {stats.approvedRecommendations} Approved
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <LightbulbIcon size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalRecommendations}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  AI Recommendations
                </div>
              </div>
            </div>
          </div>

          {/* Recommendation Savings */}
          <div className="relative border border-[var(--border-color)] bg-white">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-emerald-600">
                <ArrowUpRight size={14} />
                {formatCurrency(stats.totalActualSavings)}
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                <Award size={20} style={{ color: '#2D7DD2' }} />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {formatCurrency(stats.totalActualSavings)}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Realized Savings
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabbed Navigation */}
        <div className='border border-slate-200' style={{ marginBottom: '32px' }}>
          <div className="flex bg-white border-b border-slate-200">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
          flex items-center justify-center gap-2 px-5 py-3 w-full
          text-sm font-medium
          border-b-2 transition-colors duration-200
          ${
            activeTab === tab.id
              ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
              : 'border-transparent text-slate-500 hover:bg-slate-100'
          }
        `}
              >
                {tab.id === 'analysis' && (
                  <Target
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'recommendations' && (
                  <LightbulbIcon
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'tracking' && (
                  <TrendingUp
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.id === 'settings' && (
                  <Settings
                    size={18}
                    className={
                      activeTab === tab.id
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                )}
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {activeTab !== 'analysis' && spendAnalysis.length > 0 && (
          <div style={{ marginBottom: '24px', maxWidth: '420px' }}>
            <label style={{ display: 'grid', gap: '6px', fontWeight: 500 }}>
              Selected Analysis
              <select
                value={selectedAnalysisId}
                onChange={(event) => setSelectedAnalysisId(event.target.value)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}
              >
                <option value="">Choose analysis period</option>
                {spendAnalysis.map((analysis) => (
                  <option key={analysis._id} value={analysis._id}>
                    {analysis.analysisPeriod?.startDate
                      ? new Date(
                          analysis.analysisPeriod.startDate
                        ).toLocaleDateString()
                      : '—'}{' '}
                    to{' '}
                    {analysis.analysisPeriod?.endDate
                      ? new Date(
                          analysis.analysisPeriod.endDate
                        ).toLocaleDateString()
                      : '—'}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}

        {/* Spend Analysis Tab */}
        {activeTab === 'analysis' && (
          <div>
            {/* Analysis DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={spendAnalysis}
                columns={analysisColumns}
                title={`Spend Analysis (${spendAnalysis.length} analyses)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* AI Recommendations Tab */}
        {activeTab === 'recommendations' && (
          <div>
            {/* Recommendations DataTable */}
            <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <DataTable
                data={optimizationRecommendations}
                columns={recommendationColumns}
                title={`AI Recommendations (${optimizationRecommendations.length} recommendations)`}
                loading={loading}
                searchable={false}
                sortable={true}
                exportable={true}
                pagination={true}
                pageSize={10}
                showActions={false}
              />
            </div>
          </div>
        )}

        {/* Other Tabs */}
        {activeTab === 'tracking' && (
          <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
            <DataTable
              data={savingsTracking}
              columns={trackingColumns}
              title={`Savings Tracking (${savingsTracking.length} records)`}
              loading={loading}
              searchable={false}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={false}
            />
          </div>
        )}

        {activeTab === 'settings' && (
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb',
              minHeight: '260px',
            }}
          >
            <div style={{ display: 'grid', gap: '12px' }}>
              <div style={{ fontWeight: 600, fontSize: '16px' }}>
                Optimization Settings
              </div>
              <div style={{ color: '#4b5563' }}>
                Total Analyses: {stats.totalAnalyses}
              </div>
              <div style={{ color: '#4b5563' }}>
                Average Spend: {formatCurrency(stats.averageSpend)}
              </div>
              <div style={{ color: '#4b5563' }}>
                Estimated Savings: {formatCurrency(stats.totalEstimatedSavings)}
              </div>
            </div>
          </div>
        )}

        {showAnalysisModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowAnalysisModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                Run Spend Analysis
              </h3>
              <form onSubmit={handleAnalysisSubmit}>
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Start Date
                    <input
                      type="date"
                      value={analysisFormData.startDate}
                      onChange={(event) =>
                        setAnalysisFormData({
                          ...analysisFormData,
                          startDate: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    End Date
                    <input
                      type="date"
                      value={analysisFormData.endDate}
                      onChange={(event) =>
                        setAnalysisFormData({
                          ...analysisFormData,
                          endDate: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Currency
                    <input
                      type="text"
                      value={analysisFormData.currency}
                      onChange={(event) =>
                        setAnalysisFormData({
                          ...analysisFormData,
                          currency: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '20px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowAnalysisModal(false)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                    }}
                  >
                    Run Analysis
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showImplementModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1050,
              padding: '20px',
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                setShowImplementModal(false);
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '24px',
                width: '100%',
                maxWidth: '520px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '16px' }}>
                Track Savings
              </h3>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleImplementConfirm();
                }}
              >
                <div style={{ display: 'grid', gap: '12px' }}>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Actual Savings
                    <input
                      type="number"
                      step="0.01"
                      value={savingsFormData.actualSavings}
                      onChange={(event) =>
                        setSavingsFormData({
                          ...savingsFormData,
                          actualSavings: event.target.value,
                        })
                      }
                      required
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Status
                    <select
                      value={savingsFormData.status}
                      onChange={(event) =>
                        setSavingsFormData({
                          ...savingsFormData,
                          status: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    >
                      <option value="Implemented">Implemented</option>
                      <option value="Approved">Approved</option>
                      <option value="In Review">In Review</option>
                      <option value="Rejected">Rejected</option>
                    </select>
                  </label>
                  <label style={{ display: 'grid', gap: '6px' }}>
                    Implementation Date
                    <input
                      type="date"
                      value={savingsFormData.implementationDate}
                      onChange={(event) =>
                        setSavingsFormData({
                          ...savingsFormData,
                          implementationDate: event.target.value,
                        })
                      }
                      style={{
                        padding: '8px 10px',
                        borderRadius: '6px',
                        border: '1px solid #e5e7eb',
                      }}
                    />
                  </label>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end',
                    marginTop: '20px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setShowImplementModal(false)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: '#ffffff',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProcessing}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: 'var(--primary-color)',
                      color: '#ffffff',
                    }}
                  >
                    Save Savings
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ConfirmationModal
          isOpen={showGenerateModal}
          onClose={() => setShowGenerateModal(false)}
          onConfirm={handleApproveConfirm}
          title="Generate Recommendations"
          message="Generate AI recommendations for the selected spend analysis?"
          confirmText="Generate"
          cancelText="Cancel"
          type="success"
          isLoading={isProcessing}
        />
      </div>
    </>
  );
};

export default SpendOptimization;
